import React, { useState } from 'react';
import { Code2, Eye, GitBranch } from 'lucide-react';
import { RichMarkdown } from '../render/RichMarkdown';
import { CodeBlock, CopyButton } from '../render/CodeBlock';
import { MermaidBlock } from '../render/MermaidBlock';
import type { ArtifactViewerProps } from '../types';
import type { CodeContent, DiagramContent, MarkdownContent, ReactContent } from './shared';

// Viewer "documento": markdown ricco, codice evidenziato, diagrammi Mermaid,
// sorgente componente React (fase 1: solo sorgente, niente esecuzione).

/** Toggle riusabile anteprima/sorgente (stile segmented, zero layout shift). */
export const ViewToggle: React.FC<{
  view: 'preview' | 'source';
  onChange: (v: 'preview' | 'source') => void;
  previewLabel?: string;
}> = ({ view, onChange, previewLabel = 'Anteprima' }) => (
  <div className="inline-flex items-center rounded-md border border-border bg-card p-0.5 text-[11px]" role="tablist" aria-label="Vista artefatto">
    <button
      type="button"
      role="tab"
      aria-selected={view === 'preview'}
      onClick={() => onChange('preview')}
      className={`inline-flex items-center gap-1 rounded px-2 py-0.5 transition-colors ${view === 'preview' ? 'bg-violet-100 text-violet-800 font-medium' : 'text-muted-foreground hover:text-foreground'}`}
    >
      <Eye className="h-3 w-3" /> {previewLabel}
    </button>
    <button
      type="button"
      role="tab"
      aria-selected={view === 'source'}
      onClick={() => onChange('source')}
      className={`inline-flex items-center gap-1 rounded px-2 py-0.5 transition-colors ${view === 'source' ? 'bg-violet-100 text-violet-800 font-medium' : 'text-muted-foreground hover:text-foreground'}`}
    >
      <Code2 className="h-3 w-3" /> Sorgente
    </button>
  </div>
);

export const MarkdownViewer: React.FC<ArtifactViewerProps> = ({ artifact }) => {
  const c = artifact.content as Partial<MarkdownContent>;
  const body = typeof c?.body === 'string' ? c.body : '';
  const [view, setView] = useState<'preview' | 'source'>('preview');
  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      <div className="flex items-center justify-end gap-2 px-3 py-1.5 border-b border-border bg-muted/60">
        <ViewToggle view={view} onChange={setView} />
      </div>
      {view === 'preview' ? (
        <RichMarkdown className="max-w-none p-4 sm:p-6">
          {body}
        </RichMarkdown>
      ) : (
        <CodeBlock code={body} language="markdown" showHeader={false} className="rounded-none border-0" />
      )}
    </div>
  );
};

export const CodeViewer: React.FC<ArtifactViewerProps> = ({ artifact }) => {
  const c = artifact.content as Partial<CodeContent>;
  const language = typeof c?.language === 'string' ? c.language : 'text';
  const body = typeof c?.body === 'string' ? c.body : '';
  return <CodeBlock code={body} language={language} />;
};

export const DiagramViewer: React.FC<ArtifactViewerProps> = ({ artifact }) => {
  const c = artifact.content as Partial<DiagramContent>;
  const body = typeof c?.body === 'string' ? c.body : '';
  const syntax = c?.syntax ?? 'mermaid';
  const [view, setView] = useState<'preview' | 'source'>('preview');
  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-1.5 border-b border-border bg-muted/60">
        <GitBranch className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-xs text-muted-foreground uppercase">{syntax}</span>
        <div className="ml-auto flex items-center gap-1.5">
          <CopyButton text={body} className="inline-flex items-center gap-1 rounded px-1.5 py-1 text-[11px] text-muted-foreground hover:text-foreground hover:bg-muted transition-colors" />
          <ViewToggle view={view} onChange={setView} previewLabel="Diagramma" />
        </div>
      </div>
      {view === 'preview' ? (
        <MermaidBlock code={body} className="border-0 rounded-none" />
      ) : (
        <CodeBlock code={body} language="mermaid" showHeader={false} className="rounded-none border-0" />
      )}
    </div>
  );
};

export const ReactComponentViewer: React.FC<ArtifactViewerProps> = ({ artifact }) => {
  const c = artifact.content as Partial<ReactContent>;
  const body = typeof c?.body === 'string' ? c.body : '';
  const imports = Array.isArray(c?.imports) ? c.imports : [];
  return (
    <div className="rounded-lg overflow-hidden border border-slate-800">
      {imports.length > 0 && (
        <div className="px-3 py-1.5 bg-slate-900 border-b border-slate-800 text-[11px] text-slate-400">
          imports: {imports.join(', ')}
        </div>
      )}
      <CodeBlock code={body} language="tsx" className="rounded-none border-0" />
    </div>
  );
};
